import React, { useState } from "react";
import { PushAdminUser, PushDevice } from "@myapp/contract";
import DeleteItemDialog from "../../../shared/ui/feedback/DeleteItemDialog/DeleteItemDialog";
import { useDeleteDialog } from "../../../shared/hooks/useDeleteDialog";
import {
  fetchAllPushUserIds,
  usePushAdminStats,
  usePushAdminUsersInfiniteQuery,
  useRevokePushDevice,
} from "../../../entities/notification";
import { formatDeviceLabel } from "../lib/formatDeviceLabel";
import { usePushAdminApiFilters, usePushAdminFilters } from "../model/usePushAdminFilters";
import { usePushAdminSelection } from "../model/usePushAdminSelection";
import { PushComposeForm } from "./PushComposeForm";
import { PushMessageHistory } from "./PushMessageHistory";
import { PushUserFilterBar } from "./PushUserFilterBar";
import { PushUserList } from "./PushUserList";

/**
 * The two panes share one selection: the left list's checkboxes ARE the recipient picker,
 * so reviewing who has push and choosing who gets the message is the same act.
 *
 * Select-all and clear are quiet text links on purpose — the page has exactly one primary
 * action, and it lives on the compose form's send button.
 */
export function PushAdminBoard() {
  const [params, setParams] = usePushAdminFilters();
  const apiFilters = usePushAdminApiFilters(params);

  const usersQuery = usePushAdminUsersInfiniteQuery(apiFilters);
  const statsQuery = usePushAdminStats();
  const selection = usePushAdminSelection();
  const revokeMutation = useRevokePushDevice();
  const deleteDialog = useDeleteDialog();
  const [isSelectingAll, setIsSelectingAll] = useState(false);

  const users = usersQuery.data?.items ?? [];
  const stats = statsQuery.data;

  const handleSelectAll = async () => {
    setIsSelectingAll(true);
    try {
      // Honest "alle": walks every page of the current filter, not just the loaded ones.
      const ids = await fetchAllPushUserIds(apiFilters);
      selection.addMany(ids);
    } catch (err) {
      // No toast system in this app; failing quietly here just means the link did nothing —
      // logged so it is at least visible in the console rather than an unhandled rejection.
      console.error("Alle mit Push auswählen fehlgeschlagen", err);
    } finally {
      setIsSelectingAll(false);
    }
  };

  const handleRevokeDevice = (user: PushAdminUser, device: PushDevice) => {
    deleteDialog.openDelete(
      `Gerät von ${user.name} deaktivieren?`,
      async () => {
        await revokeMutation.mutateAsync(device.id);
      },
      {
        description: `${formatDeviceLabel(device.userAgent)} — der Nutzer kann Push jederzeit wieder aktivieren.`,
      },
    );
  };

  const linkClass =
    "text-sm font-['PT_Sans'] text-[#004494] underline-offset-2 hover:underline disabled:opacity-50";

  return (
    <div className="mt-6 flex flex-col gap-10 lg:grid lg:grid-cols-[3fr,2fr] lg:items-start lg:gap-10">
      <section>
        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <p className="text-sm font-['PT_Sans'] text-gray-500">
            {stats
              ? `${stats.usersWithPush} von ${stats.usersTotal} Nutzern mit Push · ${stats.devicesTotal} Geräte`
              : " "}
          </p>
          <div className="flex items-center gap-5">
            <button
              type="button"
              className={linkClass}
              onClick={handleSelectAll}
              disabled={isSelectingAll}
            >
              {isSelectingAll ? "Wird ausgewählt…" : "Alle mit Push auswählen"}
            </button>
            {selection.selected.size > 0 && (
              <button type="button" className={linkClass} onClick={selection.clear}>
                Auswahl aufheben
              </button>
            )}
          </div>
        </div>

        <PushUserFilterBar
          search={params.search}
          onSearchChange={(value) => setParams({ search: value })}
          roles={params.roles}
          onRolesChange={(value) => setParams({ roles: value })}
          pushStatus={params.pushStatus}
          onPushStatusChange={(value) => setParams({ pushStatus: value })}
        />

        <PushUserList
          users={users}
          isLoading={usersQuery.isLoading}
          isFetchingNextPage={usersQuery.isFetchingNextPage}
          hasNextPage={Boolean(usersQuery.hasNextPage)}
          onLoadMore={() => usersQuery.fetchNextPage()}
          selected={selection.selected}
          onToggle={selection.toggle}
          onRevokeDevice={handleRevokeDevice}
          revokeDisabled={revokeMutation.isPending}
        />
      </section>

      <section className="flex flex-col gap-10">
        <PushComposeForm selectedIds={Array.from(selection.selected)} />
        <PushMessageHistory />
      </section>

      <DeleteItemDialog deleteModal={deleteDialog} confirmLabel="Deaktivieren" />
    </div>
  );
}
